import { MONTARIA_PADRAO } from '../world/montarias';
/**
 * Regras puras do inventário: posição das células, faixas de peso e de
 * durabilidade, ordem de foco. Sem React e sem DOM — `tests/inventario.mjs`
 * importa direto.
 */

export const PACK_COLS = 5;
export const PACK_ROWS = 4;
export const PACK_SLOTS = PACK_COLS * PACK_ROWS;
export const ICON = 44;

/** `chave` é o id do slot no catálogo de nomes — nem sempre igual ao id do gump. */
export const SLOTS: ReadonlyArray<{ id: string; chave: string; ghost: string; cls: string; lockWhenDead?: boolean }> = [
  { id: 'helm', chave: 'helm', ghost: 'plate-helm', cls: 'doll-helm', lockWhenDead: true },
  { id: 'chest', chave: 'chest', ghost: 'studded-chest', cls: 'doll-chest', lockWhenDead: true },
  { id: 'arms', chave: 'arms', ghost: 'studded-arms', cls: 'doll-arms', lockWhenDead: true },
  { id: 'gloves', chave: 'gloves', ghost: 'studded-gloves', cls: 'doll-gloves', lockWhenDead: true },
  { id: 'legs', chave: 'legs', ghost: 'studded-legs', cls: 'doll-legs', lockWhenDead: true },
  { id: 'mainHand', chave: 'hand', ghost: 'katana', cls: 'doll-main' },
  { id: 'offHand', chave: 'offhand', ghost: 'heater', cls: 'doll-off' },
  { id: 'mount', chave: 'animal', ghost: MONTARIA_PADRAO, cls: 'doll-mount', lockWhenDead: true }
];
export const DEAD_LOCK = new Set(SLOTS.filter(s => s.lockWhenDead).map(s => s.id));
export const SLOT_POR_ID = new Map(SLOTS.map(s => [s.id, s]));

/**
 * Ordem na tela (esquerda → direita, cima → baixo). `SLOTS` segue na ordem de hoje. O lugar do robe saiu: a roupa
 * agora é do guarda-roupa (techspec-guardaroupa), que abre pelo botão embaixo do boneco.
 */
export const ORDEM_VISUAL = [
  'helm', 'arms', 'chest', 'gloves', 'legs', 'mainHand', 'offHand', 'mount'
] as const;

export function placeAt(ids: readonly (string | null)[], id: string, index: number): (string | null)[] {
  const next = ids.slice();
  while (next.length < PACK_SLOTS || next.length <= index) next.push(null);
  const from = next.indexOf(id);
  const occupant = next[index] ?? null;
  if (from === index) return next;
  if (from >= 0) next[from] = occupant;
  else if (occupant) {
    const empty = next.findIndex(x => x === null);
    if (empty >= 0) next[empty] = occupant;
    else next.push(occupant);
  }
  next[index] = id;
  return next;
}

export function mergeBagIds(prev: readonly (string | null)[], looseIds: readonly string[]): (string | null)[] {
  const live = new Set(looseIds);
  const next = (prev.length ? [...prev] : Array.from({ length: PACK_SLOTS }, () => null))
    .map(id => (id && live.has(id) ? id : null));
  while (next.length < PACK_SLOTS) next.push(null);
  for (const id of looseIds) {
    if (next.includes(id)) continue;
    const empty = next.findIndex(x => x === null);
    if (empty >= 0) next[empty] = id;
    else next.push(id);
  }
  return next;
}

/** Recebe só o que usa de DataTransfer. */
export function lerCorpseSlot(
  dt: Pick<DataTransfer, 'getData'>, mimeCorpse: string
): { id: number; itemId: string; qty?: number } | null {
  const raw = dt.getData(mimeCorpse) || dt.getData('text/plain');
  if (!raw || raw[0] !== '{') return null;
  try {
    const parsed = JSON.parse(raw) as { id?: unknown; itemId?: unknown; qty?: unknown };
    if (typeof parsed.id === 'number' && typeof parsed.itemId === 'string' && parsed.itemId) {
      const qty = typeof parsed.qty === 'number' && parsed.qty > 0 ? parsed.qty : undefined;
      return { id: parsed.id, itemId: parsed.itemId, ...(qty != null ? { qty } : {}) };
    }
  } catch { /* MIME forjado */ }
  return null;
}

export type FaixaPeso = 'ok' | 'perto' | 'cheio';
/** Âmbar a partir de 80% — perto do ponto em que o servidor recusa (`mochila.overweight`). */
export function faixaDoPeso(peso: number, max: number): FaixaPeso {
  if (!(max > 0)) return 'ok';
  const f = peso / max;
  return f >= 1 ? 'cheio' : f >= 0.8 ? 'perto' : 'ok';
}

export type FaixaDur = 'ok' | 'gasto' | 'quebrando';
/** Só existe com durMax > 0 (montaria vem null — PackService.cs:540). */
export function faixaDaDurabilidade(dur: number | null, max: number | null): FaixaDur | null {
  if (dur == null || max == null || max <= 0) return null;
  const f = dur / max;
  return f < 0.15 ? 'quebrando' : f < 0.4 ? 'gasto' : 'ok';
}

/** Porcentagem para a barra, presa em 0..100. */
export function fracao(a: number, b: number): number {
  return b > 0 ? Math.max(0, Math.min(100, (a / b) * 100)) : 0;
}

/** Chave i18n do verbo do botão, pelo encaixe do gump. */
export function verboDoEncaixe(slot: string): 'mochila.empunhar' | 'mochila.montar' | 'mochila.vestir' {
  if (slot === 'mainHand' || slot === 'offHand') return 'mochila.empunhar';
  if (slot === 'mount') return 'mochila.montar';
  return 'mochila.vestir';
}

/** Para onde vai o foco depois de jogar fora a célula `i`: a próxima ocupada, depois a anterior. */
export function focoDepoisDeJogar(cells: readonly unknown[], i: number): number | null {
  for (let k = i + 1; k < cells.length; k++) if (cells[k]) return k;
  for (let k = i - 1; k >= 0; k--) if (cells[k]) return k;
  return null;
}

/** Moeda é uma pilha só por personagem (o servidor não separa prata nem ouro). */
export function ehMoeda(defId: string): boolean {
  return defId === 'silver' || defId === 'gold';
}

/** A pilha dá para separar: mais de 1 e não é moeda (techspec-pilha-de-pecas §13). */
export function separavel(item: { defId: string; qty: number }): boolean {
  return item.qty > 1 && !ehMoeda(item.defId);
}

/** Mesma chave de pilha do servidor (`ChavePilha`): item, bênção, qualidade e autor. */
export function mesmaPilha(
  a: { defId: string; abencoado?: boolean; qualidade?: number; feitoPor?: string | null },
  b: { defId: string; abencoado?: boolean; qualidade?: number; feitoPor?: string | null }
): boolean {
  return a.defId === b.defId && !ehMoeda(a.defId) && !!a.abencoado === !!b.abencoado
    && (a.qualidade ?? 0) === (b.qualidade ?? 0) && (a.feitoPor ?? null) === (b.feitoPor ?? null);
}
